/**
 * O certificado com arrasto e seleção, para o editor do produtor.
 *
 * A separação importa: `CertificateVector` desenha e não sabe o que é
 * interação; este arquivo trata mouse e não sabe desenhar. O componente que
 * precisa ser IDÊNTICO em todo lugar — o desenho — não pode carregar o editor
 * dentro.
 *
 * DE ONDE VÊM AS ÁREAS DE ARRASTO
 *
 * Das primitivas que o `resolve` produziu. Cada primitiva carrega `src`, o
 * elemento que a gerou; agrupando por `src` sai a caixa de cada elemento.
 *
 * Isso não é um detalhe de implementação, é a razão de a coisa funcionar: se as
 * caixas fossem calculadas aqui, a área clicável e o desenho teriam duas contas
 * diferentes e divergiriam — a mesma doença que o pipeline existe para curar,
 * só que entre o mouse e a tinta. Elemento que não desenhou nada não tem caixa,
 * e é correto: não há o que arrastar.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  larguraDe,
  type ElementId,
  type Primitive,
  type ResolvedCertificate,
  type AssetId,
} from "@tickethall/core/certificate";
import { cn } from "@/lib/utils";

import {
  CertificateVector,
  CERTIFICATE_WIDTH,
  CERTIFICATE_HEIGHT,
} from "./CertificateVector";

export interface CertificateCanvasProps {
  resolved: ResolvedCertificate;
  assetUrl?: (id: AssetId) => string;
  mode?: "producer" | "production";
  selectedElementId?: ElementId | null;
  onSelectElement?: (id: ElementId | null) => void;
  onElementMove?: (id: ElementId, position: { x: number; y: number }) => void;
  className?: string;
  onPreviewReady?: () => void;
}

interface Caixa {
  readonly id: ElementId;
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/**
 * Largura de um texto, medida com a MESMA função que o layout usou.
 *
 * Medir de outro jeito aqui produziria uma caixa que não bate com o desenho —
 * a doença deste projeto, só que entre o mouse e a tinta. A primeira versão
 * fazia a conta à mão e dividia por 1000; o Inter tem `unitsPerEm` 2048, então
 * toda caixa saía com metade da largura.
 *
 * O `charSpacing` da primitiva está em PONTOS e `larguraDe` espera em `em`.
 */
function larguraDoTexto(p: Extract<Primitive, { k: "glyphs" }>): number {
  return larguraDe(p.text, p.font, p.size, p.size === 0 ? 0 : p.charSpacing / p.size);
}

/**
 * Caixa de cada elemento, a partir do que foi desenhado.
 *
 * Exportada para teste: é a peça que liga o mouse ao desenho, e uma conta
 * errada aqui faz o produtor arrastar um elemento e mover outro.
 */
export function caixasPorElemento(r: ResolvedCertificate): Caixa[] {
  const limites = new Map<ElementId, { x0: number; y0: number; x1: number; y1: number }>();

  const juntar = (src: string, x0: number, y0: number, x1: number, y1: number) => {
    // `background` não é elemento arrastável.
    if (src === "background") return;
    const id = src as ElementId;
    const a = limites.get(id);
    limites.set(id, a
      ? { x0: Math.min(a.x0, x0), y0: Math.min(a.y0, y0), x1: Math.max(a.x1, x1), y1: Math.max(a.y1, y1) }
      : { x0, y0, x1, y1 });
  };

  for (const p of r.primitives) {
    switch (p.k) {
      case "rect":
      case "image":
        juntar(p.src, p.x, p.y, p.x + p.w, p.y + p.h);
        break;
      case "line":
        juntar(p.src, Math.min(p.x1, p.x2), Math.min(p.y1, p.y2), Math.max(p.x1, p.x2), Math.max(p.y1, p.y2));
        break;
      case "poly":
        for (const [x, y] of p.pts) juntar(p.src, x, y, x, y);
        break;
      case "glyphs": {
        // `y` é a BASELINE. A caixa sobe pelo corpo da fonte e desce um pouco,
        // senão o clique só pega a metade de baixo das letras.
        const larg = larguraDoTexto(p);
        juntar(p.src, p.x, p.y - p.size * 0.8, p.x + larg, p.y + p.size * 0.25);
        break;
      }
    }
  }

  const escalaX = CERTIFICATE_WIDTH / r.page.width;
  const escalaY = CERTIFICATE_HEIGHT / r.page.height;

  return [...limites].map(([id, b]) => ({
    id,
    x: b.x0 * escalaX,
    y: b.y0 * escalaY,
    w: Math.max(8, (b.x1 - b.x0) * escalaX),
    h: Math.max(8, (b.y1 - b.y0) * escalaY),
  }));
}

export function CertificateCanvas({
  resolved,
  assetUrl,
  mode = "production",
  selectedElementId,
  onSelectElement,
  onElementMove,
  className,
  onPreviewReady,
}: CertificateCanvasProps) {
  const externoRef = useRef<HTMLDivElement>(null);
  const internoRef = useRef<HTMLDivElement>(null);
  const [escala, setEscala] = useState(1);
  const [arrastando, setArrastando] = useState<
    { id: ElementId; xInicial: number; yInicial: number; caixa: Caixa } | null
  >(null);

  const editavel = mode === "producer" && !!onElementMove;
  const caixas = useMemo(() => (mode === "producer" ? caixasPorElemento(resolved) : []), [resolved, mode]);

  // Encaixa o desenho no contêiner sem nunca ampliar além de 1:1.
  useEffect(() => {
    const el = externoRef.current;
    if (!el) return;
    const medir = () => {
      const { width, height } = el.getBoundingClientRect();
      if (width === 0) return;
      setEscala(Math.min(width / CERTIFICATE_WIDTH, height / CERTIFICATE_HEIGHT || Infinity, 1));
    };
    medir();
    const obs = new ResizeObserver(medir);
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    onPreviewReady?.();
  }, [resolved, onPreviewReady]);

  const aoPressionar = useCallback(
    (e: React.MouseEvent, caixa: Caixa) => {
      if (!editavel) return;
      e.preventDefault();
      e.stopPropagation();
      onSelectElement?.(caixa.id);
      setArrastando({ id: caixa.id, xInicial: e.clientX, yInicial: e.clientY, caixa });
    },
    [editavel, onSelectElement],
  );

  useEffect(() => {
    if (!arrastando || !onElementMove) return;

    const mover = (e: MouseEvent) => {
      // Dividir pela escala é o que faz o arrasto acompanhar o mouse quando o
      // preview está reduzido. Sem isso o elemento anda menos que o cursor, e
      // quanto menor a tela, pior — era um defeito registrado no plano.
      const dx = (e.clientX - arrastando.xInicial) / escala;
      const dy = (e.clientY - arrastando.yInicial) / escala;

      // A posição do elemento é o CENTRO da caixa, em porcentagem da página —
      // é o que o editor grava e o que o `resolve` lê de volta.
      const cx = arrastando.caixa.x + arrastando.caixa.w / 2 + dx;
      const cy = arrastando.caixa.y + arrastando.caixa.h / 2 + dy;

      onElementMove(arrastando.id, {
        x: Math.max(0, Math.min(100, (cx / CERTIFICATE_WIDTH) * 100)),
        y: Math.max(0, Math.min(100, (cy / CERTIFICATE_HEIGHT) * 100)),
      });
    };

    const soltar = () => setArrastando(null);
    window.addEventListener("mousemove", mover);
    window.addEventListener("mouseup", soltar);
    return () => {
      window.removeEventListener("mousemove", mover);
      window.removeEventListener("mouseup", soltar);
    };
  }, [arrastando, onElementMove, escala]);

  return (
    <div
      ref={externoRef}
      className={cn("flex items-center justify-center", className)}
      onClick={() => onSelectElement?.(null)}
    >
      <div
        className="relative select-none overflow-hidden shadow-xl"
        style={{
          width: Math.round(CERTIFICATE_WIDTH * escala),
          height: Math.round(CERTIFICATE_HEIGHT * escala),
        }}
      >
        <div
          ref={internoRef}
          data-testid="certificate-preview"
          className="absolute left-0 top-0 bg-white"
          style={{
            width: CERTIFICATE_WIDTH,
            height: CERTIFICATE_HEIGHT,
            transform: `scale(${escala})`,
            transformOrigin: "top left",
          }}
        >
          <CertificateVector
            resolved={resolved}
            assetUrl={assetUrl}
            className="absolute inset-0"
          />

          {caixas.map((c) => {
            const selecionado = selectedElementId === c.id;
            return (
              <div
                key={c.id}
                onMouseDown={(e) => aoPressionar(e, c)}
                className={cn(
                  "absolute rounded-sm border transition-colors",
                  editavel && "cursor-move",
                  selecionado
                    ? "border-primary bg-primary/10"
                    : "border-transparent hover:border-primary/40",
                )}
                style={{ left: c.x, top: c.y, width: c.w, height: c.h }}
                title={c.id}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
